// src/components/CountdownGate.jsx
// Full-site countdown gate — blocks all content until a fixed launch instant,
// then unlocks automatically with no reload and no password.

import React, { useState, useEffect } from 'react';

// Saturday 15 Aug 2026, 18:00 Irish time. Stored as a UTC instant rather than
// local time math — Ireland is on IST (UTC+1) in August, same offset as the
// UK, so 18:00 Irish time = 17:00 UTC. This avoids DST/timezone bugs for
// visitors loading the site from other timezones.
const LAUNCH_TIMESTAMP = new Date('2026-08-15T17:00:00Z').getTime();

// Soft bypass for QA before launch — not real security, just convenience.
const PREVIEW_PARAM = 'preview';
const PREVIEW_VALUE = 'maisondoud2026';

function hasPreviewBypass() {
    return new URLSearchParams(window.location.search).get(PREVIEW_PARAM) === PREVIEW_VALUE;
}

function getRemainingMs() {
    return LAUNCH_TIMESTAMP - Date.now();
}

function splitRemaining(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    return {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
    };
}

const pad = (n) => String(n).padStart(2, '0');

const CountdownGate = ({ children }) => {
    // Computed synchronously so there's no flash of the countdown UI for
    // visitors loading the site after launch (or with the preview bypass).
    const [unlocked, setUnlocked] = useState(() => hasPreviewBypass() || getRemainingMs() <= 0);
    const [remaining, setRemaining] = useState(getRemainingMs);

    useEffect(() => {
        if (unlocked) return;
        const interval = setInterval(() => {
            const next = getRemainingMs();
            setRemaining(next);
            if (next <= 0) setUnlocked(true);
        }, 1000);
        return () => clearInterval(interval);
    }, [unlocked]);

    if (unlocked) return children;

    const { days, hours, minutes, seconds } = splitRemaining(remaining);

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Tenor+Sans&display=swap');

                .countdown-root {
                    min-height: 100vh;
                    background: #0a0a0a;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 24px;
                    position: relative;
                    overflow: hidden;
                }

                .countdown-bg-orb-1 {
                    position: absolute;
                    top: -120px;
                    right: -120px;
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(206,172,143,0.06) 0%, transparent 70%);
                    pointer-events: none;
                }

                .countdown-bg-orb-2 {
                    position: absolute;
                    bottom: -120px;
                    left: -120px;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(206,172,143,0.04) 0%, transparent 70%);
                    pointer-events: none;
                }

                .countdown-divider {
                    width: 40px;
                    height: 1px;
                    background: linear-gradient(to right, transparent, #CEAC8F, transparent);
                    margin: 0 auto 32px;
                }

                .countdown-card {
                    width: 100%;
                    max-width: 480px;
                    text-align: center;
                    position: relative;
                    z-index: 1;
                }

                .countdown-wordmark {
                    font-family: 'Tenor Sans', serif;
                    color: #CEAC8F;
                    font-size: clamp(20px, 4vw, 26px);
                    letter-spacing: 0.25em;
                    font-weight: 400;
                    margin: 0 0 10px;
                }

                .countdown-subtitle {
                    color: rgba(255,255,255,0.3);
                    font-size: 10px;
                    letter-spacing: 0.3em;
                    text-transform: uppercase;
                    margin: 0 0 40px;
                }

                .countdown-box {
                    border: 1px solid rgba(206,172,143,0.15);
                    border-radius: 4px;
                    padding: 48px 40px;
                    background: rgba(255,255,255,0.02);
                    backdrop-filter: blur(10px);
                }

                .countdown-eyebrow {
                    color: rgba(206,172,143,0.5);
                    font-size: 9px;
                    letter-spacing: 0.35em;
                    text-transform: uppercase;
                    margin: 0 0 20px;
                }

                .countdown-heading {
                    font-family: 'Tenor Sans', serif;
                    color: rgba(255,255,255,0.9);
                    font-size: 22px;
                    font-weight: 400;
                    letter-spacing: 0.05em;
                    margin: 0 0 10px;
                }

                .countdown-desc {
                    color: rgba(255,255,255,0.35);
                    font-size: 13px;
                    line-height: 1.7;
                    margin: 0 0 36px;
                }

                .countdown-units {
                    display: flex;
                    justify-content: center;
                    gap: 12px;
                }

                .countdown-unit {
                    flex: 1;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(206,172,143,0.2);
                    border-radius: 2px;
                    padding: 16px 8px;
                }

                .countdown-unit-value {
                    font-family: 'Tenor Sans', serif;
                    color: #fff;
                    font-size: clamp(22px, 5vw, 30px);
                    letter-spacing: 0.05em;
                    font-variant-numeric: tabular-nums;
                }

                .countdown-unit-label {
                    color: rgba(255,255,255,0.3);
                    font-size: 9px;
                    letter-spacing: 0.2em;
                    text-transform: uppercase;
                    margin-top: 6px;
                }

                .countdown-reassurance {
                    color: rgba(206,172,143,0.6);
                    font-size: 11px;
                    letter-spacing: 0.05em;
                    margin: 24px 0 0;
                }

                .countdown-footer {
                    color: rgba(255,255,255,0.12);
                    font-size: 10px;
                    letter-spacing: 0.2em;
                    text-transform: uppercase;
                    margin-top: 40px;
                }
            `}</style>

            <div className="countdown-root">
                <div className="countdown-bg-orb-1" />
                <div className="countdown-bg-orb-2" />

                <div className="countdown-card">
                    <h1 className="countdown-wordmark">MAISON D'OUD</h1>
                    <p className="countdown-subtitle">For those who know.</p>

                    <div className="countdown-divider" />

                    <div className="countdown-box">
                        <p className="countdown-eyebrow">Launching Soon</p>
                        <h2 className="countdown-heading">The Collection Opens In</h2>

                        <div className="countdown-units">
                            <div className="countdown-unit">
                                <div className="countdown-unit-value">{pad(days)}</div>
                                <div className="countdown-unit-label">Days</div>
                            </div>
                            <div className="countdown-unit">
                                <div className="countdown-unit-value">{pad(hours)}</div>
                                <div className="countdown-unit-label">Hours</div>
                            </div>
                            <div className="countdown-unit">
                                <div className="countdown-unit-value">{pad(minutes)}</div>
                                <div className="countdown-unit-label">Min</div>
                            </div>
                            <div className="countdown-unit">
                                <div className="countdown-unit-value">{pad(seconds)}</div>
                                <div className="countdown-unit-label">Sec</div>
                            </div>
                        </div>

                        <p className="countdown-reassurance">
                            No action needed — this page will open on its own.
                        </p>
                    </div>

                    <p className="countdown-footer">© {new Date().getFullYear()} MAISON D'OUD</p>
                </div>
            </div>
        </>
    );
};

export default CountdownGate;
